// src/features/manual/utils/search.ts
//
// Client side of the manual search: the same term rules as the API (roommatik.analytics
// `ManualSearch.Terms`), so the field knows when a query is searchable, and the ranges to highlight
// in a result. Folding is one character for one, so a range in the folded text is the same range in
// the text shown.

export const MANUAL_SEARCH_MIN = 2;
export const MANUAL_SEARCH_MAX = 100;
export const MANUAL_SEARCH_DEBOUNCE_MS = 250;

const TERM_PUNCTUATION = /^["'«»“”‘’¿?¡!.,;:()[\]{}*`]+|["'«»“”‘’¿?¡!.,;:()[\]{}*`]+$/g;

/** Lower case without accents, one character for one («Medía» → «media»). */
export const foldText = (text: string): string => {
  let folded = '';
  for (const char of text) {
    if (char.length > 1) {
      // Astral characters (emoji) keep their two code units, so positions stay aligned.
      folded += char;
      continue;
    }
    const base = char.normalize('NFD')[0] ?? char;
    folded += (/\p{Mn}/u.test(base) ? char : base).toLowerCase().slice(0, 1) || char;
  }
  return folded;
};

/** The query's terms: split on spaces, surrounding punctuation removed, folded, two characters or more, no repeats. */
export const searchTerms = (query: string): string[] => {
  const terms = query
    .split(/\s+/)
    .map((term) => foldText(term.replace(TERM_PUNCTUATION, '')))
    .filter((term) => term.length >= MANUAL_SEARCH_MIN);
  return Array.from(new Set(terms));
};

/** A query the API accepts: 2–100 characters once trimmed, with at least one term. */
export const isSearchable = (query: string): boolean => {
  const trimmed = query.trim();
  return trimmed.length >= MANUAL_SEARCH_MIN && trimmed.length <= MANUAL_SEARCH_MAX && searchTerms(trimmed).length > 0;
};

export interface TextPart {
  text: string;
  hit: boolean;
}

/** `text` cut into plain and matched parts, every occurrence of every term, overlaps merged. */
export const highlightParts = (text: string, terms: readonly string[]): TextPart[] => {
  if (!text) return [];
  const folded = foldText(text);
  const ranges: Array<[number, number]> = [];
  for (const term of terms) {
    if (!term) continue;
    for (let at = folded.indexOf(term); at >= 0; at = folded.indexOf(term, at + term.length)) {
      ranges.push([at, at + term.length]);
    }
  }
  if (ranges.length === 0) return [{ text, hit: false }];

  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: Array<[number, number]> = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([...range]);
  }

  const parts: TextPart[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) parts.push({ text: text.slice(cursor, start), hit: false });
    parts.push({ text: text.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), hit: false });
  return parts;
};

interface KeyTarget {
  tagName?: string;
  isContentEditable?: boolean;
}

/** True when the keyboard is in a field, so a page shortcut must not take the key. */
export const isTypingTarget = (target: EventTarget | KeyTarget | null): boolean => {
  const element = target as KeyTarget | null;
  if (!element || typeof element.tagName !== 'string') return false;
  if (element.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName.toUpperCase());
};

interface ShortcutEvent {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  target: EventTarget | KeyTarget | null;
}

/** «/» outside a field, without modifiers: focus the manual search. */
export const isSearchShortcut = (event: ShortcutEvent): boolean =>
  event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target);

/** What a key in the search field does: Enter opens the first result, Esc empties a non-empty field. */
export const searchFieldKeyAction = (key: string, value: string): 'submit' | 'clear' | null => {
  if (key === 'Enter') return 'submit';
  if (key === 'Escape' && value) return 'clear';
  return null;
};

export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  cancel: () => void;
}

/** `fn`, called only once the calls have stopped for `delay` ms, with the last arguments. */
export const debounce = <A extends unknown[]>(fn: (...args: A) => void, delay: number): Debounced<A> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const run = ((...args: A) => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      fn(...args);
    }, delay);
  }) as Debounced<A>;
  run.cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  return run;
};
